import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { getSupabaseClient } from '../../lib/supabase';
import { 
  Calendar, 
  Receipt, 
  Wrench, 
  IndianRupee, 
  Clock, 
  CheckCircle2, 
  TrendingUp, 
  RefreshCw, 
  ArrowRight,
  ShieldAlert,
  Printer,
  ArrowDownLeft,
  ArrowUpRight,
  AlertCircle,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { ServiceBillPrintModal } from './ServiceBillPrintModal';

interface ServiceJobAlert {
  type: string;
  severity: 'danger' | 'warning' | 'info';
  message: string;
  booking_number?: string | null;
  professional?: string | null;
  item?: string | null;
  since?: string | null;
}

interface DashboardData {
  today: {
    bookings?: number;
    completed?: number;
    revenue?: number;
    collected?: number;
    expenses?: number;
    other_income?: number;
  };
  pipeline: {
    requested?: number;
    assigned?: number;
    accepted?: number;
    in_progress?: number;
    [key: string]: number | undefined;
  };
  outstanding_dues: number;
  advances_held: number;
  month: {
    revenue?: number;
    bookings?: number;
  };
  upcoming: Array<{
    booking_id: string;
    booking_number: string;
    customer_name: string;
    scheduled_at?: string | null;
    status: string;
  }>;
  recent_invoices: Array<{
    booking_id: string;
    invoice_number: string;
    customer_name: string;
    total: number;
    status: string;
  }>;
}

export function ServicesDashboardView({ 
  branchId, 
  setActiveTab 
}: { 
  branchId?: string | null; 
  setActiveTab?: (tab: string) => void;
}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const navigate = useNavigate();
  const supabase = getSupabaseClient();
  const effectiveBranchId = branchId || activeBranchId;

  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<ServiceJobAlert[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    today: { bookings: 0, completed: 0, revenue: 0, collected: 0, expenses: 0, other_income: 0 },
    pipeline: { requested: 0, assigned: 0, accepted: 0, in_progress: 0 },
    outstanding_dues: 0,
    advances_held: 0,
    month: { revenue: 0, bookings: 0 },
    upcoming: [],
    recent_invoices: []
  });
  const [printBookingId, setPrintBookingId] = useState<string | null>(null);

  const loadDashboardData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Fetch Alerts via RPC
      try {
        const { data: alertsData, error: alertsErr } = await supabase.rpc('get_service_job_alerts', {
          p_tenant_id: currentTenantId,
          p_branch_id: effectiveBranchId || null
        });
        if (alertsErr) {
          console.warn('get_service_job_alerts error:', alertsErr);
        } else if (Array.isArray(alertsData)) {
          setAlerts(alertsData);
        } else {
          setAlerts([]);
        }
      } catch (e: any) {
        console.warn('Could not fetch alerts:', e);
        setAlerts([]);
      }

      // 2. Fetch Dashboard RPC get_services_dashboard
      const { data, error } = await supabase.rpc('get_services_dashboard', {
        p_tenant_id: currentTenantId,
        p_branch_id: effectiveBranchId || null
      });

      if (error) {
        console.error('Error from get_services_dashboard:', error);
        toast.error(error.message || 'Failed to load services dashboard');
        return;
      }

      if (data) {
        setDashboardData({
          today: data.today || {},
          pipeline: data.pipeline || {},
          outstanding_dues: Number(data.outstanding_dues) || 0,
          advances_held: Number(data.advances_held) || 0,
          month: data.month || {},
          upcoming: Array.isArray(data.upcoming) ? data.upcoming : [],
          recent_invoices: Array.isArray(data.recent_invoices) ? data.recent_invoices : []
        });
      }
    } catch (err: any) {
      console.error('loadDashboardData exception:', err);
      toast.error(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId, effectiveBranchId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const goToTab = (tab: string, path: string) => {
    if (setActiveTab) {
      setActiveTab(tab);
    }
    navigate(path);
  };

  const formatCurrency = (val: number | undefined | null) => {
    const num = Number(val) || 0;
    return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatScheduledDate = (dateStr?: string | null) => {
    if (!dateStr) return 'Not scheduled';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'Not scheduled';
      return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Not scheduled';
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Top Banner Header with Quick Buttons */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 text-primary rounded-xl">
              <Wrench size={22} />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              Daily Services Dashboard
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Real-time pipeline, collections, technician alerts, and billing performance
          </p>
        </div>

        {/* Quick Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={loadDashboardData}
            disabled={loading}
            className="text-xs h-9 gap-1.5"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>

          <Button
            size="sm"
            onClick={() => goToTab('booking', '/services/booking')}
            className="bg-primary hover:bg-primary/90 text-white text-xs h-9 gap-1.5 font-semibold"
          >
            <Calendar size={14} />
            <span>New Booking</span>
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => goToTab('inward_payment', '/merchant-dashboard/services/inward-payment')}
            className="text-xs h-9 gap-1.5 font-semibold border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
          >
            <ArrowDownLeft size={14} />
            <span>Inward Payment</span>
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => goToTab('daily_expense', '/merchant-dashboard/services/daily-expense')}
            className="text-xs h-9 gap-1.5 font-semibold border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40"
          >
            <ArrowUpRight size={14} />
            <span>Daily Expense</span>
          </Button>
        </div>
      </div>

      {/* Alerts Panel (get_service_job_alerts) */}
      {alerts.length > 0 && (
        <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 space-y-3 animate-in fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-rose-500 text-white rounded-lg">
                <ShieldAlert size={16} />
              </span>
              <h2 className="text-sm font-bold text-rose-900 dark:text-rose-200">
                Action Required: Service & Equipment Alerts
              </h2>
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-rose-600 text-white">
                {alerts.length}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => goToTab('equipment', '/services/equipment')}
              className="text-xs h-7 border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40"
            >
              <span>Manage Equipment</span>
              <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {alerts.map((al, idx) => (
              <div 
                key={idx}
                className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-rose-100 dark:border-rose-900/50 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">
                      {al.type.replace(/_/g, ' ')}
                    </span>
                    {al.booking_number && (
                      <span className="font-mono text-[10px] font-semibold text-slate-500">
                        {al.booking_number}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-slate-800 dark:text-slate-200 mt-1">
                    {al.message}
                  </p>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-500">
                  <span>{al.professional || al.item || 'Service Item'}</span>
                  {al.since && <span>{new Date(al.since).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6 KPI Cards:
          Today bookings / Today revenue / Collected today / Outstanding dues / Advances held / This-month revenue
      */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 sm:gap-4">
        {/* Today Bookings */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Today Bookings
            </span>
            <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {dashboardData.today.bookings ?? 0}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
              {dashboardData.today.completed ?? 0} completed
            </div>
          </CardContent>
        </Card>

        {/* Today Revenue */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider block">
              Today Revenue
            </span>
            <div className="text-2xl font-bold text-blue-600 mt-1 font-mono">
              {formatCurrency(dashboardData.today.revenue)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              Billed service value
            </div>
          </CardContent>
        </Card>

        {/* Collected Today */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider block">
              Collected Today
            </span>
            <div className="text-2xl font-bold text-emerald-600 mt-1 font-mono">
              {formatCurrency(dashboardData.today.collected)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              Settlements + advances
            </div>
          </CardContent>
        </Card>

        {/* Outstanding Dues */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-rose-600 uppercase tracking-wider block">
              Outstanding Dues
            </span>
            <div className="text-2xl font-bold text-rose-600 mt-1 font-mono">
              {formatCurrency(dashboardData.outstanding_dues)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              Pending invoice balances
            </div>
          </CardContent>
        </Card>

        {/* Advances Held */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-purple-600 uppercase tracking-wider block">
              Advances Held
            </span>
            <div className="text-2xl font-bold text-purple-600 mt-1 font-mono">
              {formatCurrency(dashboardData.advances_held)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              Unadjusted deposits
            </div>
          </CardContent>
        </Card>

        {/* This-Month Revenue */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wider block">
              Month Revenue
            </span>
            <div className="text-2xl font-bold text-indigo-600 mt-1 font-mono">
              {formatCurrency(dashboardData.month.revenue)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              {dashboardData.month.bookings ?? 0} jobs this month
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Booking Pipeline Strip (requested -> assigned -> accepted -> in progress) */}
      <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>Service Booking Pipeline</span>
            </h3>
            <button
              onClick={() => goToTab('booking', '/services/booking')}
              className="text-xs text-primary hover:underline font-semibold flex items-center gap-1"
            >
              <span>View Bookings</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {/* Step 1: Requested */}
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-900/40">
              <div className="flex items-center justify-between text-amber-700 dark:text-amber-400">
                <span className="text-[11px] font-bold uppercase tracking-wider">1. Requested</span>
                <span className="text-xs font-mono">⏳</span>
              </div>
              <div className="text-2xl font-bold font-mono text-amber-900 dark:text-amber-200 mt-1">
                {dashboardData.pipeline.requested ?? 0}
              </div>
              <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-1">
                Awaiting technician assignment
              </p>
            </div>

            {/* Step 2: Assigned */}
            <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200/70 dark:border-blue-900/40">
              <div className="flex items-center justify-between text-blue-700 dark:text-blue-400">
                <span className="text-[11px] font-bold uppercase tracking-wider">2. Assigned</span>
                <span className="text-xs font-mono">👤</span>
              </div>
              <div className="text-2xl font-bold font-mono text-blue-900 dark:text-blue-200 mt-1">
                {dashboardData.pipeline.assigned ?? 0}
              </div>
              <p className="text-[11px] text-blue-700/80 dark:text-blue-400/80 mt-1">
                Dispatched to professional
              </p>
            </div>

            {/* Step 3: Accepted */}
            <div className="p-3 rounded-xl bg-teal-50 dark:bg-teal-950/30 border border-teal-200/70 dark:border-teal-900/40">
              <div className="flex items-center justify-between text-teal-700 dark:text-teal-400">
                <span className="text-[11px] font-bold uppercase tracking-wider">3. Accepted</span>
                <span className="text-xs font-mono">🤝</span>
              </div>
              <div className="text-2xl font-bold font-mono text-teal-900 dark:text-teal-200 mt-1">
                {dashboardData.pipeline.accepted ?? 0}
              </div>
              <p className="text-[11px] text-teal-700/80 dark:text-teal-400/80 mt-1">
                Technician confirmed dispatch
              </p>
            </div>

            {/* Step 4: In Progress */}
            <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200/70 dark:border-purple-900/40">
              <div className="flex items-center justify-between text-purple-700 dark:text-purple-400">
                <span className="text-[11px] font-bold uppercase tracking-wider">4. In Progress</span>
                <span className="text-xs font-mono">⚡</span>
              </div>
              <div className="text-2xl font-bold font-mono text-purple-900 dark:text-purple-200 mt-1">
                {dashboardData.pipeline.in_progress ?? 0}
              </div>
              <p className="text-[11px] text-purple-700/80 dark:text-purple-400/80 mt-1">
                Work currently being performed
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Grid: Upcoming Jobs & Recent Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Jobs List */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                  <Clock size={16} className="text-primary" />
                  <span>Upcoming Jobs</span>
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Scheduled bookings ready for dispatch or execution
                </p>
              </div>
              <button
                onClick={() => goToTab('booking', '/services/booking')}
                className="text-xs text-primary hover:underline font-semibold"
              >
                All Bookings
              </button>
            </div>

            {dashboardData.upcoming.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-400">
                No upcoming jobs scheduled at this moment.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {dashboardData.upcoming.map((job) => (
                  <div
                    key={job.booking_id}
                    onClick={() => goToTab('booking', '/services/booking')}
                    className="py-3 flex items-center justify-between gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 px-2 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-slate-900 dark:text-white">
                          {job.booking_number}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                          {job.status}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate mt-0.5">
                        {job.customer_name || 'Customer'}
                      </p>
                      <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                        <Clock size={11} />
                        <span>{formatScheduledDate(job.scheduled_at)}</span>
                      </p>
                    </div>

                    <ArrowRight size={14} className="text-slate-400 shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Invoices List */}
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                  <Receipt size={16} className="text-emerald-600" />
                  <span>Recent Invoices</span>
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Latest customer bills generated across services
                </p>
              </div>
              <button
                onClick={() => goToTab('billing', '/services/billing')}
                className="text-xs text-primary hover:underline font-semibold"
              >
                All Invoices
              </button>
            </div>

            {dashboardData.recent_invoices.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-400">
                No invoices generated recently.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {dashboardData.recent_invoices.map((inv) => (
                  <div
                    key={inv.booking_id + (inv.invoice_number || '')}
                    className="py-3 flex items-center justify-between gap-3 px-2 rounded-lg"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-slate-900 dark:text-white">
                          {inv.invoice_number || 'INV'}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                          inv.status === 'paid'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                            : 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400'
                        }`}>
                          {inv.status}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate mt-0.5">
                        {inv.customer_name || 'Customer'}
                      </p>
                      <p className="text-xs font-mono font-bold text-slate-900 dark:text-white mt-0.5">
                        {formatCurrency(inv.total)}
                      </p>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPrintBookingId(jobOrBookingId(inv.booking_id))}
                      className="text-xs h-8 px-2.5 gap-1 text-slate-700 dark:text-slate-300 shrink-0"
                    >
                      <Printer size={13} />
                      <span>Print</span>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bill Print Modal */}
      {printBookingId && (
        <ServiceBillPrintModal
          isOpen={Boolean(printBookingId)}
          onClose={() => setPrintBookingId(null)}
          bookingId={printBookingId}
        />
      )}
    </div>
  );
}

function jobOrBookingId(id: string): string {
  return id;
}

export default ServicesDashboardView;
